import dbg from "@utils/dbg";
import { dev } from "@utils/dev";
import { sub, unsub } from "@utils/pubsub";
import cn from "classnames";
import {
  CSSProperties,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useExternalDND from "./dnd-external";
import useInternalDND, { DragResult } from "./dnd-internal";
import { DragSide } from "./geometry";
import styles from "./index.module.scss";
import { MatrixOps, ReorderInfo, isNormalMatrix } from "./matrix";
import RowComponent, { ITEM_PADDING_COLS, ScrollInfo } from "./row";
import * as typ from "./types";
import { WithOptional } from "./utils";
//import { measureTime } from "./item";

export type Item = WithOptional<typ.Item, "pos">;
const isItemData = (item: Item): item is typ.Item =>
  (item as typ.Item).pos !== undefined;

export interface GroupProps {
  items: Item[];
  unit: number;
  dragType: string;
  minColumns: number;
  found: Set<string>;
  scrollX: number;

  className?: string;
  rowClassName?: string;
  style?: CSSProperties;
  unlocked?: boolean;
  hasSearch?: boolean;
  scrollTarget?: HTMLDivElement;

  extractTransferItemSize: (
    ev: React.DragEvent<HTMLDivElement>
  ) => [number, number];
  onDragEnter?: React.DragEventHandler<HTMLDivElement>;
  onDragLeave?: React.DragEventHandler<HTMLDivElement>;
  onDragOver?: React.DragEventHandler<HTMLDivElement>;
  onDrop?: React.DragEventHandler<HTMLDivElement>;
  onReorder?: (info: ReorderInfo[]) => void;
  onAlign?: (info?: ReorderInfo[]) => void;
}

const makeEmptyRow = (rowOffset: number): typ.Row => {
  const result = {
    offset: rowOffset,
    length: 0,
    items: [],
    at: () => undefined,
  };

  (result as any).normal = true;
  return result;
};

const makeEmptyMatrix = (): typ.Matrix => {
  const result = {
    rows: [makeEmptyRow(0)],
    idToItem: {},
  };

  (result as any).normal = true;
  return result;
};

const matrixOps = new MatrixOps(makeEmptyMatrix, makeEmptyRow);

const useGroupControl = (props: GroupProps) => {
  //measureTime.start();

  const [matrix, setMatrix] = useState<typ.Matrix>(makeEmptyMatrix());
  const self = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<CSSProperties>();
  const extDragResult = useRef<DragResult>();
  const [scrollRow, setScrollRow] = useState<number>();
  const [scrollID, setScrollID] = useState<string>();
  const [groupWidth, setGroupWidth] = useState(0);

  const onDrop = (res: DragResult) => {
    const ops = matrixOps.withData(matrix);
    const [newMatrix, reorder] = ops.move(
      res.dragged.id,
      res.over.pos,
      res.side || DragSide.left,
      res.over.id
    );

    if (props.onReorder && reorder !== undefined) {
      //console.log("REORDER");
      props.onReorder(reorder);
    } else {
      //console.log("SET", isNormalMatrix(newMatrix));
      setMatrix(newMatrix as typ.Matrix);
    }
  };

  const registerRowWidth = (w: number) => setGroupWidth((x) => Math.max(w, x));

  useEffect(
    () => setStyle({ minWidth: `${groupWidth + 100}px` }),
    [groupWidth]
  );

  const extractTransferNumCols = useCallback(
    (ev: typ.GroupDragEvent): number => {
      const [_, cols] = props.extractTransferItemSize(ev);
      return cols + ITEM_PADDING_COLS;
    },
    [props.extractTransferItemSize, matrix]
  );

  const dragger = useInternalDND(matrix, props.unit, onDrop);
  const extDragger = useExternalDND(matrix, props.unit, extractTransferNumCols);
  const extDraggerRef = useRef<typeof extDragger>();

  const dragStart = (
    ev: typ.ItemMouseEvent,
    el: MutableRefObject<typ.HTMLItemElement>,
    item: typ.Item
  ): void => {
    dragger.dragStart(ev, el, item);
  };

  const itemExtDragOver = (ev: typ.ItemDragEvent, item: typ.Item): void => {
    extDragger.dragOver(ev);
  };

  const extDragOverGroup = (e: typ.GroupDragEvent) => {
    props.onDragOver && props.onDragOver(e);
    if (!e.dataTransfer.items[0]) return;
    if (e.dataTransfer.items[0].type !== props.dragType) return;
    extDragger.dragOver(e);
  };

  const extDragLeaveGroup = (e: typ.GroupDragEvent) => {
    props.onDragLeave && props.onDragLeave(e);
    if (!e.dataTransfer.items[0]) return;
    if (e.dataTransfer.items[0].type !== props.dragType) return;
    if (self.current !== e.target) return;
    extDragger.dragLeave();
  };

  useEffect(() => {
    extDraggerRef.current = extDragger;
  }, [extDragger]);

  const doAlign = useCallback(() => {
    const ops = matrixOps.withData(matrix);
    const [_, reorder] = ops.align();
    props.onAlign && props.onAlign(reorder);
    if (reorder === undefined) {
      dbg.log("ALIGN FAILED");
    }
  }, [props.onAlign, matrix]);

  useEffect(() => {
    const f = sub("location-align-racks", doAlign);
    return () => unsub("location-align-racks", f);
  }, [doAlign]);

  useEffect(() => {
    const ops = matrixOps.withData(makeEmptyMatrix());
    const storeItems = props.items.map((it) => ({ ...it }));

    for (const item of storeItems) {
      item.numCols += ITEM_PADDING_COLS;
      if (isItemData(item)) {
        //dbg.log("ITEM!", item);
        ops.set(item, false);
      } else {
        //dbg.log("NOTITEM!", item);
      }
    }

    ops.withData(ops.normalize());
    //dbg.log("HERE", ops.data.rows);
    const reorder = ops.withReorderControl(() => {
      for (const item of storeItems) {
        if (!isItemData(item)) {
          const dres = extDragResult.current;
          //dbg.log("ADD ITEM", dres?.over.pos);
          ops.add(item, dres?.over.pos, dres?.side);
          if (extDraggerRef.current) extDraggerRef.current.stop();
        }
      }
    });

    if (reorder !== undefined && props.onReorder) {
      //dbg.log("BEFORE REORDER", reorder);
      props.onReorder(reorder);
    } else {
      const m = ops.normalize();
      //dbg.log("SET MATRIX", m.rows);
      setMatrix(m);
    }
  }, [props.items]);

  useEffect(() => {
    if (!isNormalMatrix(matrix)) {
      dev.err("[Reorder2D.Group] error: Abnormal matrix", matrix);
    }
    const ops = matrixOps.withData(matrix);
    for (let row = 0; row < matrix.rows.length; row++) {
      const r = matrix.rows[row];
      r.at = (col) => ops.at(row, col);
    }
  }, [matrix]);

  useEffect(() => {
    if (props.hasSearch) {
      loop1: for (let row = 0; row < matrix.rows.length; row++) {
        const r = matrix.rows[row];
        if (props.hasSearch) {
          for (const item of r.items) {
            if (!item?.id) continue;
            if (props.found.has(item.id)) {
              setScrollRow(row);
              setScrollID(item.id);
              break loop1;
            }
          }
        }
      }
    } else {
      setScrollRow(undefined);
      setScrollID(undefined);
    }
  }, [matrix, props.hasSearch]);

  const extDrop = useCallback(
    (e: typ.GroupDragEvent) => {
      //console.log("SET EXT DRAG RESULT", extDragger.dragged);
      props.onDrop && props.onDrop(e);
      extDragResult.current = extDragger.dragged;
    },
    [props.onDrop, extDragger.dragged]
  );

  const scrollInfo: ScrollInfo = useMemo(
    () => ({
      needScroll: props.hasSearch,
      target: props.scrollTarget,
      row: scrollRow,
      id: scrollID,
    }),
    [props.hasSearch, props.scrollTarget, scrollRow, scrollID]
  );

  //useEffect(() => console.log("SCROLL GROUP", scrollInfo, props.hasSearch, props.found), [scrollInfo, props.hasSearch, props.found]);

  return {
    matrix,
    self,
    style,
    dragged: dragger.dragged,
    dragPos: dragger.dragPos,
    extDragged: extDragger.dragged,
    scrollInfo,
    groupWidth,
    dragStart,
    itemExtDragOver,
    extDragOverGroup,
    extDragLeaveGroup,
    extDrop,
    registerRowWidth,
  };
};

const Group = (props: GroupProps) => {
  const ctrl = useGroupControl(props);
  return (
    <div
      className={cn(props.className, styles.group)}
      style={{ ...props.style, ...ctrl.style }}
      onDragEnter={props.onDragEnter}
      onDragLeave={(ev) => ctrl.extDragLeaveGroup(ev)}
      onDragOver={(ev) => ctrl.extDragOverGroup(ev)}
      onDrop={(ev) => ctrl.extDrop(ev)}
      ref={ctrl.self}
    >
      {ctrl.matrix.rows.map((row, idx) => (
        <RowComponent
          key={idx}
          row={row}
          columnWidth={props.unit}
          rowIndex={idx}
          dragType={props.dragType}
          dragged={ctrl.dragged}
          extDragged={ctrl.extDragged}
          dragPos={ctrl.dragPos}
          className={props.rowClassName}
          unlocked={props.unlocked}
          dragStart={ctrl.dragStart}
          itemExtDragOver={ctrl.itemExtDragOver}
          scroll={ctrl.scrollInfo}
          scrollX={props.scrollX}
          registerRowWidth={ctrl.registerRowWidth}
          groupWidth={ctrl.groupWidth}
        />
      ))}
    </div>
  );
};

export default Group;
